import { useEffect, useState } from 'react'
import {
  fetchSummary,
  fromUnix,
  planName,
  priceLine,
  setCancellation,
} from '../membership'
import type { Subscription } from '../membership'
import type { CovellaUser } from '../session'
import { formatDate, isMember, readRole } from '../session'

/**
 * Her account, and her membership, managed here rather than on a second page.
 *
 * The app deliberately says nothing about membership, so everything a woman needs in order to
 * see what she is paying, when it renews, or to stop paying, has to live on this website. It used
 * to live on `/join/`; this is that page's management half, brought into the portal so there is
 * one place to go. Checkout has not moved, because the Stripe links it depends on are still
 * placeholders.
 *
 * Cancelling asks once before it acts. It is her decision and the button should not be hard to
 * find, but it is also not a thing to do by brushing past it, and the confirmation is where the
 * "you keep everything until the date" promise gets said plainly.
 */
export function AccountPanel({
  user,
  offline,
  onSignOut,
}: {
  user: CovellaUser
  offline: boolean
  onSignOut: () => void
}) {
  const member = isMember(user)
  const renews = user.covella_member_until ? formatDate(user.covella_member_until) : null
  const since = formatDate(user.created)

  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!member) return
    let cancelled = false
    void fetchSummary().then((value) => {
      if (!cancelled) setSubscription(value)
    })
    return () => {
      cancelled = true
    }
  }, [member])

  const ending = subscription?.cancelAtPeriodEnd === true
  const periodEnd = fromUnix(subscription?.currentPeriodEnd) ?? renews

  async function changeCancellation(cancel: boolean) {
    setBusy(true)
    setError(null)
    setNote(null)
    try {
      await setCancellation(cancel)
      setSubscription(await fetchSummary())
      setConfirming(false)
      setNote(
        cancel
          ? `Cancelled. You keep everything until ${periodEnd ?? 'the end of the period'}, and nothing more is charged.`
          : 'Your membership is running again.',
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : "That didn't go through. Please try again.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="stack">
      {offline && (
        <p className="notice">
          Could not reach the server just now, so this may be out of date. You are still signed
          in.
        </p>
      )}

      <div className="pair">
        <div className="card">
          <h2 className="account-name">{user.name || 'Your account'}</h2>
          <p className="account-email">{user.email}</p>

          <dl className="rows">
            <div>
              <dt>Account</dt>
              <dd>Torahly, {readRole(user)}</dd>
            </div>
            {since && (
              <div>
                <dt>Member since</dt>
                <dd>{since}</dd>
              </div>
            )}
          </dl>
        </div>

        <div className="card">
          <div className="keeper-head">
            <h2 className="panel-title">The Keepers</h2>
            <span className={member ? 'badge badge-on' : 'badge'}>
              {ending ? 'Ending' : member ? 'Active' : 'Not a member'}
            </span>
          </div>

          {member ? (
            <>
              <p className="panel-note">
                {ending
                  ? 'You keep everything until the date below. Nothing more will be charged.'
                  : 'Everything is unlocked in the app. Covellah reads this the next time you open it.'}
              </p>

              <dl className="rows">
                {subscription && (
                  <div>
                    <dt>Plan</dt>
                    <dd>{planName(subscription)}</dd>
                  </div>
                )}
                {subscription && priceLine(subscription) && (
                  <div>
                    <dt>Price</dt>
                    <dd>{priceLine(subscription)}</dd>
                  </div>
                )}
                <div>
                  <dt>{ending ? 'Access ends' : periodEnd ? 'Next payment' : 'Expires'}</dt>
                  <dd>{periodEnd ?? 'Never'}</dd>
                </div>
                {subscription?.cardBrand && subscription?.cardLast4 && (
                  <div>
                    <dt>Card</dt>
                    <dd>
                      {subscription.cardBrand.charAt(0).toUpperCase() +
                        subscription.cardBrand.slice(1)}{' '}
                      ending {subscription.cardLast4}
                    </dd>
                  </div>
                )}
              </dl>

              {note && <p className="notice">{note}</p>}
              {error && <p className="notice">{error}</p>}

              {ending ? (
                <button
                  className="btn-outline account-act"
                  type="button"
                  disabled={busy}
                  onClick={() => changeCancellation(false)}
                >
                  {busy ? 'One moment' : 'Start it again'}
                </button>
              ) : confirming ? (
                <div className="confirm">
                  <p className="confirm-ask">
                    You keep everything until {periodEnd ?? 'the end of this period'}, then it
                    stops. Nothing is charged after that and you can come back any time.
                  </p>
                  <div className="confirm-acts">
                    <button
                      className="btn-outline"
                      type="button"
                      disabled={busy}
                      onClick={() => changeCancellation(true)}
                    >
                      {busy ? 'Cancelling' : 'Yes, cancel it'}
                    </button>
                    <button
                      className="btn-quiet inline"
                      type="button"
                      onClick={() => setConfirming(false)}
                    >
                      Keep my membership
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="btn-quiet inline account-act"
                  type="button"
                  onClick={() => setConfirming(true)}
                >
                  Cancel membership
                </button>
              )}
            </>
          ) : (
            <>
              <p>
                Everything the Torah asks of you is free in Covellah, and always will be. Your cycle
                counts, the feast dates, every reading plan, your journal and your prayers.
              </p>
              <p className="panel-note">
                Membership adds the conveniences, hands-free rep counting, cycle insights and meal
                planning, and pays for the server the rest runs on.
              </p>
              <a className="btn-primary link-button" href="https://api.torahly.app/join/">
                Join the Keepers
              </a>
            </>
          )}
        </div>
      </div>

      <button className="btn-outline" type="button" onClick={onSignOut}>
        Sign out
      </button>
    </section>
  )
}
